// O'Brien Contracting Business Constants

export const COMPANY = {
  name: "O'Brien Contracting",
  legalName: "O'Brien Contracting, LLC",
  tagline: "North Port Roofing Contractor Serving Venice & Southwest Florida",
  phone: "239-919-4485",
  phoneFormatted: "(239) 919-4485",
  phoneE164: "+12399194485",
  phoneHref: "tel:+12399194485",
  telHref: "tel:+12399194485",
  smsHref: "sms:+12399194485",
  // All phone CTAs (FL, GA, all micro-sites) route to the single business line.
  gaPhone: "239-919-4485",
  gaPhoneFormatted: "(239) 919-4485",
  gaPhoneE164: "+12399194485",
  gaPhoneHref: "tel:+12399194485",
  gaTelHref: "tel:+12399194485",
  gaSmsHref: "sms:+12399194485",
  // Owner verified 2026-07-13 (PF-OD-04 RESOLVED). Safe for public display and schema.
  email: "support@obriencontractingusa.com",
  emailVerified: true as boolean,
  license: "CCC1335947",
  address: {
    street: "4063 Fonsica Ave",
    city: "North Port",
    state: "FL",
    zip: "34286",
    full: "4063 Fonsica Ave, North Port, FL 34286",
  },
  founded: 2016,
  yearsExperience: new Date().getFullYear() - 2016,
  // Live values from the Google Business Profile (verify periodically).
  googleRating: 5.0,
  googleReviews: 19,
  // Facebook / Instagram / TikTok / Yelp verified by owner 2026-07-10.
  // Source of truth for schema sameAs arrays is
  // src/config/business.ts (BUSINESS_SAME_AS).
  social: {
    facebook: "https://www.facebook.com/profile.php?id=61563400679102",
    instagram: "https://www.instagram.com/obriencontractingusa/",
    tiktok: "https://www.tiktok.com/@ob.contracting.us",
    yelp: "https://www.yelp.com/biz/o-brien-contracting-north-port",
  },
} as const;

/**
 * PUBLIC_EMAIL — single gate for public rendering of the support mailbox.
 * Returns `null` until the mailbox is verified (inbound, outbound, reply,
 * spam placement, SPF/DKIM/DMARC, and form-delivery test). Consumers MUST
 * check for null:
 *   - JSX:      {PUBLIC_EMAIL && <a href={`mailto:${PUBLIC_EMAIL}`}>...</a>}
 *   - Schemas:  ...(PUBLIC_EMAIL ? { email: PUBLIC_EMAIL } : {})
 * Do NOT read COMPANY.email directly on public surfaces.
 */
export const PUBLIC_EMAIL: string | null = COMPANY.emailVerified ? COMPANY.email : null;

// Primary cities for pillar pages and consistent referencing
export const PRIMARY_CITIES = [
  { name: "North Port", slug: "north-port" },
  { name: "Venice", slug: "venice" },
  { name: "Englewood", slug: "englewood" },
  { name: "Port Charlotte", slug: "port-charlotte" },
  { name: "Sarasota", slug: "sarasota" },
  { name: "Bradenton", slug: "bradenton" },
] as const;

// Florida service-area counties (North Port + Gulf Coast first)
export const SERVICE_AREAS = [
  { name: "North Port", slug: "north-port-fl", description: "North Port, 34286, 34287, 34288, 34289", region: "Core Market" },
  { name: "Venice", slug: "venice-fl", description: "Venice, South Venice, Venice Gardens, Nokomis", region: "Core Market" },
  { name: "Englewood", slug: "englewood", description: "Englewood and nearby Gulf Coast communities", region: "Core Market" },
  { name: "Port Charlotte", slug: "port-charlotte", description: "Port Charlotte and nearby Charlotte County neighborhoods", region: "Core Market" },
  { name: "Sarasota", slug: "sarasota", description: "Sarasota and nearby Sarasota County communities", region: "Core Market" },
  { name: "Bradenton", slug: "bradenton", description: "Bradenton and nearby Manatee County communities", region: "Core Market" },
] as const;

export const SERVICES = [
  { name: "Metal Roofing", slug: "metal-roofing", icon: "Zap", description: "Durable, energy-efficient metal roofing for North Port, Venice, and nearby Florida homes." },
  { name: "Tile Roofing", slug: "tile-roofing", icon: "Home", description: "Long-lasting tile roof systems built for Southwest Florida sun, wind, and storms." },
  { name: "Shingle Roofing", slug: "shingle-roofing", icon: "Layers", description: "Architectural shingle installations and replacements for North Port and Venice homes." },
  { name: "Flat Roofing", slug: "flat-roofing", icon: "Square", description: "Residential and light commercial flat roof systems for the local Gulf Coast climate." },
  { name: "Roof Repair", slug: "roof-repair", icon: "Wrench", description: "Fast, reliable repairs for all roof types." },
  { name: "Roof Replacement", slug: "roof-replacement", icon: "RefreshCw", description: "Complete roof replacement with quality materials." },
  { name: "Roof Inspection", slug: "roof-inspection", icon: "Search", description: "Comprehensive inspections and detailed reports." },
  { name: "Hurricane Damage", slug: "hurricane-damage", icon: "Wind", description: "Storm damage repair and insurance claim assistance." },
  { name: "Emergency Repairs", slug: "emergency-roof-repair", icon: "AlertTriangle", description: "24/7 emergency roof repair services." },
  { name: "Commercial Roofing", slug: "commercial-roofing", icon: "Building", description: "Full-service commercial roofing solutions." },
] as const;

export const TRUST_SIGNALS = [
  { title: `${new Date().getFullYear() - 2016}+ Years Experience`, icon: "Award" },
  { title: "Licensed & Insured", subtitle: "CCC1335947", icon: "Shield" },
  { title: "Lifetime Workmanship", icon: "CheckCircle" },
  { title: "Free Estimates", icon: "FileText" },
  { title: "Local Family Owned", icon: "Heart" },
  { title: "24/7 Emergency Service", icon: "Clock" },
] as const;

export const TESTIMONIALS = [
  {
    name: "Christopher Wieser",
    rating: 5,
    text: "I couldn't be happier with my new roof! From start to finish, O'Brien Contracting exceeded my expectations. They did a fantastic job!",
    location: "Marco Island",
    datePublished: "2024-11-15",
  },
  {
    name: "Cathy Rhoads",
    rating: 5,
    text: "Just beautiful and so helpful. Glad to work with a company that was so responsive!",
    location: "Naples",
    datePublished: "2024-10-22",
  },
  {
    name: "Daniel Rodriguez",
    rating: 5,
    text: "Excellent service from start to finish. The team was professional and completed the work on time. Highly recommend!",
    location: "Fort Myers",
    datePublished: "2024-09-08",
  },
  {
    name: "Mary Johnson",
    rating: 5,
    text: "O'Brien Contracting replaced our entire roof after hurricane damage. They handled the insurance claim and made the process seamless.",
    location: "Cape Coral",
    datePublished: "2024-08-14",
  },
  {
    name: "Robert Thompson",
    rating: 5,
    text: "Great experience! The crew was respectful of our property and did an amazing job on our new metal roof.",
    location: "Naples",
    datePublished: "2024-07-30",
  },
] as const;

// Helper to group service areas by region
export const getServiceAreasByRegion = () => {
  const grouped: Record<string, typeof SERVICE_AREAS[number][]> = {};
  SERVICE_AREAS.forEach(area => {
    if (!grouped[area.region]) {
      grouped[area.region] = [];
    }
    grouped[area.region].push(area);
  });
  return grouped;
};

// Mapping of area slugs to location search terms for photo filtering
export const AREA_TO_LOCATIONS: Record<string, string[]> = {
  "collier-county": ["Naples", "Marco Island", "Immokalee", "Collier"],
  "lee-county": ["Fort Myers", "Cape Coral", "Bonita Springs", "Estero", "Lee"],
  "charlotte-county": ["Port Charlotte", "Punta Gorda", "Charlotte"],
  "sarasota-county": ["Sarasota", "Venice", "North Port", "Englewood"],
  "manatee-county": ["Bradenton", "Palmetto", "Lakewood Ranch", "Manatee"],
  "hillsborough-county": ["Tampa", "Brandon", "Plant City", "Temple Terrace", "Hillsborough"],
  "pinellas-county": ["St. Petersburg", "Clearwater", "Largo", "Pinellas Park", "Pinellas"],
  "north-port-fl": ["North Port", "34286", "34287", "34288", "34289"],
  "venice-fl": ["Venice", "South Venice", "Venice Gardens", "Nokomis"],
  "englewood": ["Englewood"],
  "port-charlotte": ["Port Charlotte"],
  "sarasota": ["Sarasota"],
  "bradenton": ["Bradenton"],
};

// Get primary location for an area slug (for display purposes)
export const getPrimaryLocationForArea = (slug: string): string => {
  const locations = AREA_TO_LOCATIONS[slug];
  return locations ? locations[0] : slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// FAQ Items for homepage and schema
export const FAQ_ITEMS = [
  {
    question: "How long does a roof replacement take?",
    answer: "Most residential roof replacements take 1-3 days depending on the size and complexity. Larger or more complex projects may take up to a week. We'll provide a specific timeline during your free estimate."
  },
  {
    question: "Do you offer financing options?",
    answer: "Yes! We offer flexible financing options to help make your roofing project affordable. We also work directly with insurance companies on storm damage claims."
  },
  {
    question: "What roofing materials do you recommend for Florida?",
    answer: "Metal roofing and tile are excellent choices for Florida's climate, offering superior wind resistance and energy efficiency. We also install high-quality architectural shingles rated for hurricane conditions."
  },
  {
    question: "Are you licensed and insured?",
    answer: "Absolutely. O'Brien Contracting is fully licensed (CCC1335947), bonded, and insured. We carry comprehensive liability insurance and workers' compensation for your protection."
  },
  {
    question: "Do you provide free estimates?",
    answer: "Yes, we provide completely free, no-obligation estimates. One of our roofing experts will inspect your roof and provide detailed pricing within 24-48 hours."
  },
  {
    question: "What is your warranty policy?",
    answer: "We offer a lifetime workmanship warranty on all installations, plus manufacturer warranties on materials ranging from 25-50 years depending on the product selected."
  },
  {
    question: "How do I know if I need a new roof or just repairs?",
    answer: "Signs you may need a new roof include: the roof is over 20 years old, you see widespread shingle damage, there are multiple leaks, or sagging areas. Our free inspection will provide honest recommendations."
  },
  {
    question: "Do you handle insurance claims for storm damage?",
    answer: "Yes! We have extensive experience working with insurance companies on hurricane and storm damage claims. We'll document the damage, meet with adjusters, and help ensure you receive fair compensation."
  }
] as const;
